import {
    getPipeValue,
    getScopeFromLang,
    getLangFromScope,
    MaybeArray,
    TranslocoScope,
    TranslocoService,
    isScopeObject,
    toCamelCase,
    ProviderScope,
    InlineLoader,
    hasInlineLoader,
} from '@ngneat/transloco';
import { OperatorFunction, Observable } from 'rxjs';
import { take } from 'rxjs/operators';

interface LangResolverParams {
    inline?: string;
    provider?: string;
    active: string;
}

export class LangResolver {
    initialized = false;

    // inline => provider => active
    resolve({ inline, provider, active }: LangResolverParams): string {
        let lang = active;
        /**
         * When the user changes the lang we need to update
         * the view. Otherwise, the lang will remain the inline/provided lang
         */
        if (this.initialized) {
            lang = active;

            return lang;
        }

        if (provider) {
            const [, extracted] = getPipeValue(provider, 'static');
            lang = extracted;
        }

        if (inline) {
            const [, extracted] = getPipeValue(inline, 'static');
            lang = extracted;
        }

        this.initialized = true;

        return lang;
    }

    /**
     *
     * Resolve the lang
     *
     * @example
     *
     * resolveLangBasedOnScope('todos/en') => en
     * resolveLangBasedOnScope('en') => en
     *
     */
    resolveLangBasedOnScope(lang: string) {
        const scope = getScopeFromLang(lang);

        return scope ? getLangFromScope(lang) : lang;
    }

    /**
     *
     * Resolve the lang path for loading
     *
     * @example
     *
     * resolveLangPath('todos', 'en') => todos/en
     * resolveLangPath('en') => en
     *
     */
    resolveLangPath(lang: string, scope?: string) {
        return scope ? `${scope}/${lang}` : lang;
    }
}

interface ScopeResolverParams {
    inline: string | undefined;
    provider: MaybeArray<TranslocoScope>;
}

export class ScopeResolver {
    constructor(private translocoService: TranslocoService) {}

    // inline => provider
    resolve(
        { inline, provider }: ScopeResolverParams = {
            inline: undefined,
            provider: undefined,
        },
    ): string | undefined {
        if (inline) {
            return inline;
        }

        if (provider) {
            if (isScopeObject(provider)) {
                const { scope, alias = toCamelCase(scope) } = provider as ProviderScope;
                this.translocoService._setScopeAlias(scope, alias);

                return scope;
            }

            return provider as string;
        }

        return undefined;
    }
}

export function shouldListenToLangChanges(service: TranslocoService, lang?: string) {
    const [hasStatic] = getPipeValue(lang, 'static');
    if (!hasStatic) {
        // If we didn't get 'lang|static' check if it's set in the global level
        return !!service.config.reRenderOnLangChange;
    }

    // We have 'lang|static' so don't listen to lang changes
    return false;
}

export function resolveInlineLoader(providerScope: TranslocoScope | null, scope?: string): InlineLoader | undefined {
    return hasInlineLoader(providerScope) ? prependScope(providerScope.loader!, scope!) : undefined;
}

export function listenOrNotOperator<T>(listenToLangChange?: boolean): OperatorFunction<T, T> {
    return listenToLangChange ? (source: Observable<T>) => source : take<T>(1);
}

export function prependScope(inlineLoader: InlineLoader, scope: string) {
    return Object.keys(inlineLoader).reduce((acc, lang) => {
        acc[`${scope}/${lang}`] = inlineLoader[lang];

        return acc;
    }, {} as Record<string, InlineLoader[keyof InlineLoader]>);
}
